import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const NAV_LINKS = [
  { to: '/features', label: 'Features' },
  { to: '/how-it-works', label: 'How It Works' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/faq', label: 'FAQ' },
];

const STEPS = [
  {
    num: '01',
    icon: '🧩',
    title: 'Install the Chrome Extension',
    summary: 'One click. 30 seconds. You\'re live.',
    body: 'Head to the Chrome Web Store, click "Add to Chrome", and Prorately is instantly active in your browser. No IT department needed, no configuration wizard, no setup calls. The moment it\'s installed, it\'s ready to detect quoting forms.',
    detail: 'Works on Chrome, Brave, and any Chromium-based browser. Also available as a Firefox extension.',
    color: '#3B82F6',
  },
  {
    num: '02',
    icon: '📥',
    title: 'Import Your Client Book',
    summary: 'Bring your existing data in minutes.',
    body: 'Upload a CSV from your current spreadsheet or CRM, and Prorately maps your columns automatically. Alternatively, connect directly via native integrations with AgencyZoom, Salesforce, HubSpot, or Applied Epic. Most agents have their full book imported in under 5 minutes.',
    detail: 'Prorately never deletes or overwrites existing records — imports are always additive and fully reversible.',
    color: '#8B5CF6',
  },
  {
    num: '03',
    icon: '🔍',
    title: 'Search or Open a Client Record',
    summary: 'Find anyone in under 2 seconds.',
    body: 'Use the Prorately dashboard or the extension\'s built-in search to pull up any client by name, vehicle, address, or policy number. The full profile — drivers, vehicles, properties, prior coverage, notes — is all right there.',
    detail: 'You can also create a new client on the fly directly from the extension popup without leaving the carrier portal.',
    color: '#06B6D4',
  },
  {
    num: '04',
    icon: '🌐',
    title: 'Open Any Carrier Portal',
    summary: 'Navigate to your quoting platform as normal.',
    body: 'Go to EZLynx, Progressive, State Farm, Travelers, or any of the 40+ supported portals. Prorately sits silently in the background, watching for forms. The moment it detects a quoting form, the extension activates automatically.',
    detail: 'No tab switching, no separate window — Prorately lives directly inside your existing browser tab.',
    color: '#10B981',
  },
  {
    num: '05',
    icon: '⚡',
    title: 'Watch It Autofill',
    summary: 'Every field filled in under 3 seconds.',
    body: 'Prorately maps your client\'s data to the carrier\'s form fields in real time. Name, DOB, address, driver\'s license, vehicle details, prior coverage — all pre-populated instantly. You review, make any tweaks, and hit submit. That\'s it.',
    detail: 'All fills are logged automatically, so you have a full audit trail of every quoting session.',
    color: '#F59E0B',
  },
  {
    num: '06',
    icon: '📊',
    title: 'Track, Follow Up, Renew',
    summary: 'Your pipeline runs itself.',
    body: 'Every quote is saved to the client\'s record and your pipeline dashboard. Set the status — Quoted, Pending, Sold. Prorately tracks renewal dates and fires automated alerts at 90, 60, and 30 days out so no client ever slips through the cracks.',
    detail: 'Agency plan users get team-wide pipeline views, manager reporting, and renewal leaderboards.',
    color: '#EF4444',
  },
];

const FAQS_HOW = [
  { q: 'Do I need any technical skills to set it up?', a: 'None at all. If you can install a Chrome extension and upload a CSV, you\'re all set. Most agents are fully operational within 10 minutes of signing up.' },
  { q: 'What happens if a carrier changes their form layout?', a: 'Our engineering team monitors all 40+ carrier portals continuously. When a carrier updates their form, we push an extension update — usually within 24 hours.' },
  { q: 'Can I use Prorately on multiple computers?', a: 'Yes. Your account syncs across any device you\'re logged into. Install the extension on your work machine, laptop, and home computer — they all stay in sync.' },
  { q: 'Does the extension slow down my browser?', a: 'No measurable impact. The extension only activates when it detects a supported quoting form — it\'s completely dormant otherwise.' },
];

function SharedNav({ mobileOpen, setMobileOpen }) {
  return (
    <>
      <style>{`
        .nav{position:sticky;top:0;z-index:100;background:rgba(255,255,255,0.92);backdrop-filter:blur(16px);border-bottom:1px solid #E2E8F0;padding:0 2rem;height:68px;display:flex;align-items:center;justify-content:space-between;}
        .nav-logo{font-family:'Sora',sans-serif;font-size:1.25rem;font-weight:800;color:#2563EB;letter-spacing:-0.5px;text-decoration:none;display:flex;align-items:center;gap:0.4rem;}
        .nav-links a{color:#475569;font-size:0.9rem;font-weight:500;text-decoration:none;margin-left:2rem;transition:color 0.2s;}
        .nav-links a:hover{color:#2563EB;}
        .btn-nav{background:#2563EB;color:#fff;padding:0.55rem 1.4rem;border-radius:8px;font-size:0.9rem;font-weight:600;text-decoration:none;transition:background 0.2s,transform 0.15s;border:none;cursor:pointer;}
        .btn-nav:hover{background:#1D4ED8;transform:translateY(-1px);}
        .hamburger{display:none;background:none;border:1px solid #E2E8F0;border-radius:8px;padding:0.4rem 0.6rem;cursor:pointer;font-size:1.2rem;}
        @media(max-width:768px){
          .nav-links,.nav-desktop-cta{display:none !important;}
          .hamburger{display:block;}
          .mobile-menu{position:fixed;top:68px;left:0;right:0;background:#fff;border-bottom:1px solid #E2E8F0;padding:1.5rem 2rem;z-index:99;box-shadow:0 8px 32px rgba(0,0,0,0.08);}
          .mobile-menu a{display:block;padding:0.75rem 0;color:#0F172A;font-size:1rem;font-weight:500;text-decoration:none;border-bottom:1px solid #f1f5f9;}
          .mobile-menu a:last-child{border-bottom:none;}
        }
      `}</style>
      <nav className="nav">
        <Link to="/" className="nav-logo">⚡ Prorately</Link>
        <div className="nav-links" style={{ display: 'flex', alignItems: 'center' }}>
          {NAV_LINKS.map(l => <Link key={l.to} to={l.to}>{l.label}</Link>)}
        </div>
        <div className="nav-desktop-cta" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <Link to="/login" style={{ color: '#475569', fontWeight: 500, textDecoration: 'none', fontSize: '0.9rem' }}>Login</Link>
          <Link to="/register" className="btn-nav">Start Free Trial</Link>
        </div>
        <button className="hamburger" onClick={() => setMobileOpen(!mobileOpen)}>☰</button>
      </nav>
      {mobileOpen && (
        <div className="mobile-menu">
          {NAV_LINKS.map(l => <Link key={l.to} to={l.to} onClick={() => setMobileOpen(false)}>{l.label}</Link>)}
          <Link to="/login" onClick={() => setMobileOpen(false)}>Login</Link>
          <Link to="/register" onClick={() => setMobileOpen(false)} style={{ color: '#2563EB', fontWeight: 700 }}>Start Free Trial →</Link>
        </div>
      )}
    </>
  );
}

export default function HowItWorks() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div style={{ fontFamily: "'DM Sans','Segoe UI',sans-serif", background: '#F8FAFF', color: '#0F172A', overflowX: 'hidden' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=Sora:wght@400;600;700;800&display=swap');
        *{box-sizing:border-box;margin:0;padding:0;}
        html{scroll-behavior:smooth;}
        .sora{font-family:'Sora',sans-serif;}

        .page-hero{background:linear-gradient(135deg,#1E3A8A 0%,#2563EB 60%,#3B82F6 100%);padding:5rem 2rem;text-align:center;position:relative;overflow:hidden;}
        .page-hero::before{content:'';position:absolute;inset:0;background-image:linear-gradient(rgba(255,255,255,0.05) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.05) 1px,transparent 1px);background-size:40px 40px;}
        .page-hero-content{position:relative;z-index:1;max-width:700px;margin:0 auto;}
        .page-tag{display:inline-block;background:rgba(255,255,255,0.15);border:1px solid rgba(255,255,255,0.3);color:#fff;font-size:0.75rem;font-weight:700;text-transform:uppercase;letter-spacing:0.08em;padding:0.35rem 0.9rem;border-radius:100px;margin-bottom:1.25rem;}
        .page-hero h1{font-family:'Sora',sans-serif;font-size:clamp(2rem,4vw,3.2rem);font-weight:800;color:#fff;line-height:1.15;letter-spacing:-0.03em;margin-bottom:1rem;}
        .page-hero p{color:rgba(255,255,255,0.8);font-size:1.1rem;line-height:1.7;max-width:560px;margin:0 auto 2rem;}
        .btn-white{background:#fff;color:#2563EB;padding:0.85rem 2rem;border-radius:12px;font-weight:700;font-size:0.95rem;text-decoration:none;border:none;cursor:pointer;transition:all 0.2s;display:inline-flex;align-items:center;gap:0.5rem;}
        .btn-white:hover{background:#EFF6FF;transform:translateY(-1px);box-shadow:0 8px 24px rgba(0,0,0,0.12);}

        /* Steps */
        .steps-section{max-width:900px;margin:0 auto;padding:5rem 2rem;}
        .step-block{display:grid;grid-template-columns:80px 1fr;gap:2rem;margin-bottom:4rem;align-items:start;}
        @media(max-width:640px){.step-block{grid-template-columns:1fr;gap:1rem;}}
        .step-left{display:flex;flex-direction:column;align-items:center;gap:0.75rem;}
        .step-num-badge{font-family:'Sora',sans-serif;font-size:0.8rem;font-weight:800;width:52px;height:52px;border-radius:14px;display:flex;align-items:center;justify-content:center;border:2px solid;}
        .step-icon{font-size:1.5rem;}
        .step-connector{width:2px;height:60px;background:linear-gradient(to bottom,#E2E8F0,transparent);}
        .step-block:last-child .step-connector{display:none;}
        .step-right{}
        .step-title{font-family:'Sora',sans-serif;font-weight:800;font-size:1.2rem;color:#0F172A;margin-bottom:0.3rem;}
        .step-summary{font-size:0.9rem;font-weight:600;margin-bottom:0.75rem;}
        .step-body{color:#475569;font-size:0.95rem;line-height:1.7;margin-bottom:0.75rem;}
        .step-detail{display:flex;align-items:flex-start;gap:0.5rem;background:#F8FAFF;border:1px solid #E2E8F0;border-radius:8px;padding:0.65rem 0.9rem;font-size:0.82rem;color:#64748B;line-height:1.5;}
        .step-detail-icon{flex-shrink:0;color:#2563EB;font-size:0.85rem;}

        /* FAQ */
        .hiw-faq{background:#fff;padding:5rem 2rem;}
        .hiw-faq-inner{max-width:720px;margin:0 auto;}
        .section-tag{display:inline-block;background:#EFF6FF;color:#2563EB;font-size:0.75rem;font-weight:700;text-transform:uppercase;letter-spacing:0.08em;padding:0.35rem 0.9rem;border-radius:100px;border:1px solid #BFDBFE;margin-bottom:1rem;}
        .section-title{font-family:'Sora',sans-serif;font-size:clamp(1.8rem,3.5vw,2.4rem);font-weight:800;line-height:1.2;letter-spacing:-0.02em;color:#0F172A;margin-bottom:2.5rem;}
        .faq-item{border-bottom:1px solid #E2E8F0;padding:1.5rem 0;}
        .faq-question{display:flex;justify-content:space-between;align-items:center;cursor:pointer;gap:1rem;}
        .faq-q-text{font-weight:600;color:#0F172A;font-size:0.98rem;}
        .faq-chevron{color:#94A3B8;font-size:1rem;transition:transform 0.25s;flex-shrink:0;}
        .faq-chevron.open{transform:rotate(180deg);color:#2563EB;}
        .faq-answer{color:#64748B;font-size:0.92rem;line-height:1.7;max-height:0;overflow:hidden;transition:max-height 0.35s ease,padding-top 0.25s;}
        .faq-answer.open{max-height:200px;padding-top:0.75rem;}

        /* CTA */
        .page-cta{background:#0F172A;padding:5rem 2rem;text-align:center;}
        .page-cta h2{font-family:'Sora',sans-serif;font-size:clamp(1.8rem,3.5vw,2.5rem);font-weight:800;color:#fff;margin-bottom:1rem;letter-spacing:-0.02em;}
        .page-cta p{color:#64748B;font-size:1rem;margin-bottom:2rem;max-width:480px;margin-left:auto;margin-right:auto;line-height:1.7;}
        .btn-blue{background:#2563EB;color:#fff;padding:0.9rem 2rem;border-radius:12px;font-weight:700;font-size:1rem;text-decoration:none;border:none;cursor:pointer;transition:all 0.2s;display:inline-flex;align-items:center;gap:0.5rem;box-shadow:0 4px 16px rgba(37,99,235,0.3);}
        .btn-blue:hover{background:#1D4ED8;transform:translateY(-1px);}

        footer{background:#0F172A;color:#94A3B8;padding:3rem 2rem 2rem;border-top:1px solid #1E293B;}
        .footer-bottom-simple{max-width:1100px;margin:0 auto;display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:1rem;font-size:0.82rem;}
        .footer-bottom-simple a{color:#64748B;text-decoration:none;margin-left:1.5rem;}
        .footer-bottom-simple a:hover{color:#93C5FD;}
      `}</style>

      <SharedNav mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />

      <div className="page-hero">
        <div className="page-hero-content">
          <span className="page-tag">How It Works</span>
          <h1 className="sora">From zero to autofilling quotes in under 10 minutes</h1>
          <p>No onboarding calls. No IT setup. Just install, import, and start quoting faster than you ever thought possible.</p>
          <Link to="/register" className="btn-white">🚀 Get Started Free</Link>
        </div>
      </div>

      <div className="steps-section">
        {STEPS.map((s, i) => (
          <div className="step-block" key={s.num}>
            <div className="step-left">
              <div className="step-num-badge" style={{ color: s.color, borderColor: `${s.color}40`, background: `${s.color}10` }}>{s.num}</div>
              <div className="step-icon">{s.icon}</div>
              {i < STEPS.length - 1 && <div className="step-connector" />}
            </div>
            <div className="step-right">
              <div className="step-title">{s.title}</div>
              <div className="step-summary" style={{ color: s.color }}>{s.summary}</div>
              <p className="step-body">{s.body}</p>
              <div className="step-detail">
                <span className="step-detail-icon">ℹ</span>
                {s.detail}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="hiw-faq">
        <div className="hiw-faq-inner">
          <span className="section-tag">Setup FAQ</span>
          <h2 className="section-title sora">Common questions about getting started</h2>
          {FAQS_HOW.map((faq, i) => (
            <div className="faq-item" key={i}>
              <div className="faq-question" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                <span className="faq-q-text">{faq.q}</span>
                <span className={`faq-chevron${openFaq === i ? ' open' : ''}`}>▾</span>
              </div>
              <div className={`faq-answer${openFaq === i ? ' open' : ''}`}>{faq.a}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="page-cta">
        <h2 className="sora">Simple enough to start today.</h2>
        <p>14-day free trial. No credit card. No onboarding call needed. Just sign up and go.</p>
        <Link to="/register" className="btn-blue">Start Free Trial →</Link>
      </div>

      <footer>
        <div className="footer-bottom-simple">
          <span>© {new Date().getFullYear()} Prorately. All rights reserved.</span>
          <div>
            <a href="#">Privacy</a>
            <a href="#">Terms</a>
            <a href="#">Security</a>
          </div>
        </div>
      </footer>
    </div>
  );
}